import axios from 'axios'
import * as React from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import { Link } from 'react-router-dom'
import Button from '~components/ui/Button'
import Pagination from '~components/ui/Pagination'
import DateFilter from '~components/ui/DateFilter'
import { displayPrice } from '~helpers/price'
import { debounce } from 'lodash'
import classNames from 'classnames'
import Sprite from '~components/Sprite'
import { getOrderStatus } from '~helpers/main'
import { pageView } from '~helpers/ga'

class Orders extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      loading: false,
      currentPage: 1,
      totalPages: 1,
      orders: [],
      search: '',
      fromDate: null,
      toDate: null,
      sort: 'newest',
      reset: false
    }

    this.handleSearchChange = this.handleSearchChange.bind(this)
    this.loadOrdersDebounced = debounce(this.loadOrders, 500, { leading: false })
  }

  componentDidMount () {
    pageView()

    this.loadOrders()
  }

  loadOrders (page = 1) {
    if (this.state.loading) {
      return
    }

    this.setState({
      loading: true
    })

    const params = {
      page,
      sort: this.state.sort
    }

    if (this.state.search) {
      params.search = this.state.search
    }

    if (this.state.fromDate) {
      params.from_date = moment(this.state.fromDate).format('YYYY-MM-DD')
    }

    if (this.state.toDate) {
      params.to_date = moment(this.state.toDate).format('YYYY-MM-DD')
    }

    axios.get('/api/account/orders/all', { params })
      .then(response => {
        this.setState({
          orders: response.data.data,
          currentPage: response.data.current_page,
          totalPages: response.data.last_page
        })
      })
      .finally(() => {
        this.setState({
          loading: false
        })
      })
  }

  loadNextPage (page = 1) {
    if (this.state.loading) {
      return
    }

    this.setState({
      loading: true
    })

    axios.get('/api/account/orders/all', { params: { page } })
      .then(response => {
        this.setState({
          orders: response.data.data,
          currentPage: response.data.current_page,
          totalPages: response.data.last_page
        })
      })
      .finally(() => {
        this.setState({
          loading: false
        })
      })
  }

  handlePageChange (i) {
    if (this.state.totalPages >= i && i > 0 && i !== this.state.currentPage) {
      this.loadNextPage(i)
    }
  }

  handleSearchChange (event) {
    this.setState({
      search: event.target.value
    }, () => this.loadOrdersDebounced(1))
  }

  handleSortChange (event) {
    this.setState({
      sort: event.target.value
    }, () => this.loadOrders(1))
  }

  handleDatePickerChange (field, value) {
    if (['fromDate', 'toDate'].indexOf(field) === -1) {
      return
    }

    this.setState({
      [field]: value,
      reset: true
    }, () => this.loadOrders(1))
  }

  resetDateFilters () {
    this.handleDatePickerChange('fromDate', undefined)
    this.handleDatePickerChange('toDate', undefined)

    this.setState({
      reset: false
    })
  }

  render () {
    return (
      <>
        <section className="w-full mb-10">
          <h3 className="text-left text-md border-b border-coral pb-4">
            Order history
          </h3>

          <div className="w-full mt-8 mb-13">
            <div className="flex flex-wrap justify-between -mx-2 -my-2">
              <div className="w-full px-2 mt-2 mb-6 lg:my-2 lg:w-1/3 xxl:w-1/4">
                <div className="relative flex-1 z-40 h-12">
                  <input
                    className="relative block text-charcoal bg-lightGrey focus:bg-white focus:border-coral duration-200 ease-in-out h-full w-full transition-colors rounded-3xl p-4 pr-12 border border-pink color-charcoal placeholder-charcoal outline-none z-10"
                    type="text"
                    name="ordersSearch"
                    value={this.state.search}
                    onChange={this.handleSearchChange}
                    disabled={this.state.busy}
                    placeholder="Search"
                  />

                  <button className="w-10 h-full flex flex-col cursor-pointer absolute top-0 bottom-0 right-1 z-20" type="submit" value="submit" aria-label="Submit">
                    <span className="block m-auto w-6 h-6" aria-hidden="true">
                      <Sprite name="search" />
                    </span>
                  </button>
                </div>
              </div>

              <div className="w-full px-2 my-2 lg:w-2/3 xxl:w-3/5">
                <div className="flex flex-wrap -mx-2 -my-2">
                  <div className="w-full md:w-1/3 px-2 my-2">
                    <select
                      className="w-full transition-colors duration-150 ease-in-out h-12 pl-4 pr-10 cursor-pointer border-grey border rounded bg-chevron-down bg-no-repeat bg-right-4 bg-white bg-3 appearance-none outline-none focus:border-charcoal hover:border-charcoal"
                      placeholder="Sort by"
                      value={this.state.sort}
                      onChange={this.handleSortChange.bind(this)}
                      disabled={this.state.busy}
                    >
                      <option value="newest">
                        Newest first
                      </option>

                      <option value="oldest">
                        Oldest first
                      </option>
                    </select>
                  </div>

                  <div className="w-full md:w-1/3 px-2 my-2">
                    <DateFilter
                      className="w-full block transition-colors duration-150 ease-in-out h-12 px-4 pt-3 cursor-pointer border-grey border rounded bg-no-repeat bg-3 appearance-none outline-none focus:border-charcoal hover:border-charcoal"
                      value={this.state.fromDate}
                      onChange={value => this.handleDatePickerChange('fromDate', value)}
                      placeholder="From date"
                      showPlaceholder={this.state.showPlaceholder}
                    />
                  </div>

                  <div className="w-full md:w-1/3 px-2 my-2">
                    <DateFilter
                      className="w-full block transition-colors duration-150 ease-in-out h-12 px-4 pt-3 cursor-pointer border-grey border rounded bg-no-repeat bg-3 appearance-none outline-none focus:border-charcoal hover:border-charcoal"
                      value={this.state.toDate}
                      onChange={value => this.handleDatePickerChange('toDate', value)}
                      placeholder="To date"
                    />
                  </div>

                  {this.state.reset && (
                    <div className="w-full md:w-1/3 px-2 my-2 ml-auto md:text-right">
                      <button
                        type="button"
                        onClick={() => this.resetDateFilters()}
                        className="no-underline hover:underline focus:underline"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-250">
              <thead className="border-b border-pink">
                <tr>
                  <th className="p-4 sm:p-6 pr-0 text-left">
                    ID
                  </th>

                  <th className="p-4 sm:p-6 pr-0 text-left">
                    Date
                  </th>

                  <th className="p-4 sm:p-6 pr-0 text-left">
                    Amount
                  </th>

                  <th className="p-4 sm:p-6 pr-0 text-left">
                    Status
                  </th>

                  <th className="p-4 sm:p-6 pr-0 text-left">
                    Customer ref
                  </th>

                  <th className="p-4 sm:p-6 pr-0 text-right"></th>
                </tr>
              </thead>

              <tbody>
                {this.state.orders.map((order, index) => {
                  const classes = []

                  if (index % 2 === 0) {
                    classes.push('bg-pink')
                  }

                  return (
                    <tr key={order.id}>
                      <td className={classNames('p-4 sm:px-6 pr-0 border-b border-l border-t border-pink', classes)}>
                        {order.type === 'online' &&
                        <Link to={`/account/orders/${order.id}`}>
                          {order.id}
                        </Link>}
                        {order.type === 'offline' && `${order.id}`}
                      </td>

                      <td className={classNames('p-4 sm:px-6 pr-0 border-b border-t border-pink', classes)}>
                        {moment(order.date).format('DD-MM-YYYY')}
                      </td>

                      <td className={classNames('p-4 sm:px-6 pr-0 border-b border-t border-pink', classes)}>
                        {displayPrice(order.amount, order.currency)}
                      </td>

                      <td className={classNames('p-4 sm:px-6 pr-0 border-b border-t border-pink', classes)}>
                        {getOrderStatus(order.status)}
                      </td>

                      <td className={classNames('p-4 sm:px-6 pr-0 border-b border-t border-pink', classes)}>
                        {order.ref}
                      </td>

                      <td className={classNames('p-4 sm:px-6 text-right border-b border-t border-r border-pink', classes)}>
                        <Button type="button" className="w-auto">
                          Resend confirmation
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            totalPages={this.state.totalPages}
            onClick={this.handlePageChange.bind(this)}
            currentPage={this.state.currentPage}
            className="mb-10 xl:my-20"
          />
        </section>
      </>
    )
  }
}

export default connect()(Orders)
